import { formatDistanceToNow, parseISO } from 'date-fns';
import { T, Var, useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import type {
  CashReceiptLifecycle,
  CashReceiptListNote,
  CashReceiptListTask,
} from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { useUsersInOrg } from '@/api/rpc/hooks/core/useUsersInOrg';
import { useAllAgentDefinitions } from '@/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { useCan } from '@/hooks/useCan';
import { formatDateOnlyString } from '@/utils/dateUtils';
import {
  formatLifecycleStatus,
  formatLogOutcome,
  formatLogTaskAssignee,
  formatLogTaskStatus,
  lifecycleStatusClass,
  logTaskStatusClass,
  logTaskTiming,
} from './cashReceiptTaskFormat';

/**
 * Where the receipt is in being filed, with the outcome when it closed some
 * other way than keyed ("Closed · Duplicate").
 */
export function CashReceiptLifecycleBadge({
  lifecycle,
  className = '',
}: {
  lifecycle: CashReceiptLifecycle;
  className?: string;
}) {
  const gt = useGT();
  const label = formatLifecycleStatus(lifecycle.status, gt);
  return (
    <Badge
      variant="outline"
      className={`shrink-0 ${lifecycleStatusClass(lifecycle.status)} ${className}`}
    >
      {lifecycle.status === 'closed' && lifecycle.outcome
        ? `${label} · ${formatLogOutcome(lifecycle.outcome, gt)}`
        : label}
    </Badge>
  );
}

/**
 * Name of whoever the task is assigned to. Both directories are org-wide
 * cached queries, so every row shares one fetch each; agents are only read
 * when the viewer may see them.
 */
function useTaskAssigneeName(task: CashReceiptListTask): string | null {
  const [canReadAgents] = useCan(Action.Read, Subject.BackgroundAgent);
  const { data: users } = useUsersInOrg();
  const { data: agents } = useAllAgentDefinitions({
    enabled: canReadAgents && task.assigneeType === 'agent',
  });
  if (task.assigneeUserId) {
    return users?.find((u) => u.id === task.assigneeUserId)?.name ?? null;
  }
  if (task.assigneeAgentDefinitionId) {
    return (
      agents?.definitions.find((a) => a.id === task.assigneeAgentDefinitionId)
        ?.name ?? null
    );
  }
  return null;
}

/** Who has the receipt's logging task; a dash when it never had one. */
export function CashReceiptAssigneeCell({
  task,
}: {
  task: CashReceiptListTask | null;
}) {
  if (!task) {
    return <span className="text-muted-foreground">—</span>;
  }
  return <TaskAssigneeName task={task} />;
}

function TaskAssigneeName({ task }: { task: CashReceiptListTask }) {
  const gt = useGT();
  const name = formatLogTaskAssignee(task, useTaskAssigneeName(task), gt);
  return (
    <span
      className={`block truncate text-[13px] ${
        task.assigneeType ? '' : 'text-muted-foreground'
      }`}
      title={name}
    >
      {name}
    </span>
  );
}

/**
 * The receipt's logging task at a glance: status (with the outcome once
 * closed) on top, then the task id and its due date — or when it was closed.
 */
export function CashReceiptTaskCell({
  task,
}: {
  task: CashReceiptListTask | null;
}) {
  const gt = useGT();
  if (!task) {
    return (
      <span className="text-[13px] italic text-muted-foreground">
        <T context="cash receipts list, receipt has never had a logging task">
          No task
        </T>
      </span>
    );
  }
  return <TaskSummary task={task} gt={gt} />;
}

function TaskSummary({
  task,
  gt,
}: {
  task: CashReceiptListTask;
  gt: ReturnType<typeof useGT>;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="flex min-w-0 items-center gap-1.5">
        <Badge
          variant="outline"
          className={`shrink-0 text-xs ${logTaskStatusClass(task.status)}`}
        >
          {formatLogTaskStatus(task.status, gt)}
        </Badge>
        {task.outcome && (
          <span className="truncate text-xs text-muted-foreground">
            {formatLogOutcome(task.outcome, gt)}
          </span>
        )}
      </span>
      <TaskMetaLine task={task} />
    </div>
  );
}

/**
 * "AR-12 · due 10/09/2026" or "AR-12 · 2 days ago" (closed). The assignee has
 * its own column.
 */
function TaskMetaLine({ task }: { task: CashReceiptListTask }) {
  const timing = logTaskTiming(task);
  return (
    <span className="truncate text-[11px] text-muted-foreground">
      {task.displayId}
      {task.displayId && timing && ' · '}
      {timing?.kind === 'due' && (
        <T context="cash receipts list, open task due date">
          due <Var>{formatDateOnlyString(timing.dueDate, '')}</Var>
        </T>
      )}
      {timing?.kind === 'closed' &&
        formatDistanceToNow(parseISO(timing.closedAt), { addSuffix: true })}
    </span>
  );
}

/**
 * The newest note on the receipt — what someone last said about it — with
 * its author and age, mirroring the collection-threads "last note" column.
 */
export function CashReceiptLatestNoteCell({
  note,
  wrap = false,
}: {
  note: CashReceiptListNote | null;
  /** Wrap the note onto two lines instead of cutting it to one. */
  wrap?: boolean;
}) {
  const gt = useGT();
  if (!note) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="flex min-w-0 flex-col">
      <span
        className={
          wrap
            ? 'line-clamp-2 text-[13px] break-words whitespace-normal'
            : 'truncate text-[13px]'
        }
        title={note.body}
      >
        {note.body}
      </span>
      <span className="truncate text-[11px] text-muted-foreground">
        {note.actorName ??
          gt('System', {
            context: 'fallback author label for a system-authored note',
          })}
        {' · '}
        {formatDistanceToNow(parseISO(note.occurredAt), { addSuffix: true })}
      </span>
    </div>
  );
}
